import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  setHours,
  setMinutes,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'

interface Props {
  value: Date | null
  onChange: (date: Date) => void
  minDate?: Date
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = [0, 15, 30, 45]
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export function DateTimePicker({ value, onChange, minDate }: Props): React.JSX.Element {
  const [viewMonth, setViewMonth] = useState(value ?? new Date())
  const [selectedHour, setSelectedHour] = useState(value ? value.getHours() : 9)
  const [selectedMinute, setSelectedMinute] = useState(value ? value.getMinutes() : 0)

  const monthStart = startOfMonth(viewMonth)
  const monthEnd = endOfMonth(viewMonth)
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
  const days = eachDayOfInterval({ start: calStart, end: calEnd })

  const selectedDate = value ?? null

  function handleDayClick(day: Date): void {
    if (minDate && isBefore(day, startOfDay(minDate))) return
    const withTime = setMinutes(setHours(day, selectedHour), selectedMinute)
    onChange(withTime)
  }

  function handleTimeChange(hour: number, minute: number): void {
    setSelectedHour(hour)
    setSelectedMinute(minute)
    if (selectedDate) {
      onChange(setMinutes(setHours(selectedDate, hour), minute))
    }
  }

  return (
    <div className="space-y-4">
      {/* Calendar header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setViewMonth(subMonths(viewMonth, 1))}
          className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-1"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold text-text-primary">
          {format(viewMonth, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth(addMonths(viewMonth, 1))}
          className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-1"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-1 text-center text-[11px] font-medium text-text-tertiary">
            {d}
          </div>
        ))}

        {/* Day cells */}
        {days.map((day) => {
          const inMonth = isSameMonth(day, viewMonth)
          const selected = selectedDate && isSameDay(day, selectedDate)
          const today = isToday(day)
          const disabled = minDate ? isBefore(day, startOfDay(minDate)) : false

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => handleDayClick(day)}
              disabled={disabled}
              className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${
                selected
                  ? 'bg-brand-500 font-semibold text-white'
                  : disabled
                    ? 'text-surface-3 cursor-not-allowed'
                    : today
                      ? 'font-semibold text-brand-600 hover:bg-brand-50'
                      : inMonth
                        ? 'text-text-primary hover:bg-surface-1'
                        : 'text-text-tertiary hover:bg-surface-1'
              }`}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>

      {/* Time picker */}
      <div className="border-t border-surface-3 pt-3">
        <p className="mb-2 text-xs font-medium text-text-secondary">Time</p>
        <div className="flex items-center gap-2">
          <select
            value={selectedHour}
            onChange={(e) => handleTimeChange(parseInt(e.target.value, 10), selectedMinute)}
            className="w-20 rounded-lg border border-surface-3 bg-surface-0 px-2 py-2 text-center text-sm font-medium text-text-primary focus:border-brand-500 focus:outline-none"
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {h.toString().padStart(2, '0')}
              </option>
            ))}
          </select>
          <span className="text-lg font-bold text-text-secondary">:</span>
          <select
            value={selectedMinute}
            onChange={(e) => handleTimeChange(selectedHour, parseInt(e.target.value, 10))}
            className="w-20 rounded-lg border border-surface-3 bg-surface-0 px-2 py-2 text-center text-sm font-medium text-text-primary focus:border-brand-500 focus:outline-none"
          >
            {MINUTES.map((m) => (
              <option key={m} value={m}>
                {m.toString().padStart(2, '0')}
              </option>
            ))}
          </select>
          <span className="text-xs text-text-tertiary">
            {selectedDate ? format(selectedDate, 'EEEE, d MMMM') : 'Pick a date'}
          </span>
        </div>
      </div>

      {/* Summary */}
      {selectedDate && (
        <div className="rounded-lg bg-brand-50 px-3 py-2 text-center text-sm font-medium text-brand-700">
          {format(selectedDate, "EEEE, d MMMM yyyy 'at' HH:mm")}
        </div>
      )}
    </div>
  )
}
